// stagePlannedAmount.js — what a fixed design stage is planned to bill.
//
// The rule, same as the Invoices page Stage Billing table: a lump sum entered
// on the stage wins; without one the stage bills its percentage of the base
// design fee. The percentage stays on a lump-sum stage as a note only. RF is
// the case that needs it: separate AD and ID fees, each stage carrying a lump
// sum plus a % of its own track's fee, so the percentages add up to 150% of
// the combined fee while the lump sums add up to exactly 100%.
//
// All amounts are in the project's contract currency (the unit `budget` and
// `billing.lumpsum` are stored in). Callers that work in INR convert the
// result. Pure: no React, no FX.

/** Lump sum entered on a fixed stage's billing, or 0. */
export function getStageLumpsum(project, stageKey) {
  const billing = project?.stageTimeline?.[stageKey]?.billing || {};
  return Number(billing.lumpsum ?? billing.lumpSum) || 0;
}

/** Stage % of the base fee: invoicePercent, else budgetAllocation. */
export function getStagePercent(project, stageKey) {
  if (!project || !stageKey) return 0;
  const invoicePercent = project.stageTimeline?.[stageKey]?.invoicePercent;
  if (invoicePercent != null) return Number(invoicePercent) || 0;
  return Number(project.budgetAllocation?.[stageKey]) || 0;
}

/** Planned amount of a fixed stage: lump sum if entered, else % × base fee. */
export function getStagePlannedAmount(project, stageKey, baseFee = Number(project?.budget) || 0) {
  const lumpsum = getStageLumpsum(project, stageKey);
  if (lumpsum > 0) return lumpsum;
  return (baseFee * getStagePercent(project, stageKey)) / 100;
}

/**
 * The stage's share of the base fee, in percent. A lump sum stage reports
 * lump sum / fee, so shares add up to what will actually be billed rather than
 * to whatever the percentage notes say. Falls back to the percentage when the
 * fee is 0, since no share can be computed from it.
 */
export function getStageFeeShare(project, stageKey, baseFee = Number(project?.budget) || 0) {
  const lumpsum = getStageLumpsum(project, stageKey);
  if (lumpsum > 0 && baseFee > 0) return (lumpsum / baseFee) * 100;
  return getStagePercent(project, stageKey);
}
